
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SettingsLayout } from '../../components/SettingsLayout';
import { VerifiedUser, Key, DesktopWindows, Smartphone } from '@mui/icons-material';
import { Loader2, Check } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

export const SecurityScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [twoFactor, setTwoFactor] = useState(true);

  const handleSave = () => {
    setLoading(true);
    setTimeout(() => {
        setLoading(false);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 2000);
    }, 1500);
  };

  const handleSignOutSession = () => {
      alert(t('securitySettings.signOutAlert'));
  };

  return (
    <SettingsLayout>
      <div className="max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="mb-10">
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">{t('securitySettings.title')}</h1>
            <p className="text-slate-500 mt-2 text-base">{t('securitySettings.subtitle')}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-10 space-y-12">
                <div className="flex items-start justify-between gap-6">
                    <div className="flex gap-5">
                        <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                            <VerifiedUser className="text-primary" />
                        </div>
                        <div className="max-w-xl">
                            <h3 className="text-base font-semibold text-slate-900">{t('securitySettings.twoFactor.title')}</h3>
                            <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{t('securitySettings.twoFactor.desc')}</p>
                        </div>
                    </div>
                    <div className="pt-2">
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                checked={twoFactor}
                                onChange={() => setTwoFactor(!twoFactor)}
                                className="sr-only peer"
                                type="checkbox"
                            />
                            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                        </label>
                    </div>
                </div>
                <div className="pt-10 border-t border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-5">
                        <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Key className="text-slate-600" />
                        </div>
                        <div>
                            <h3 className="text-base font-semibold text-slate-900">{t('securitySettings.password.title')}</h3>
                            <p className="text-sm text-slate-500 mt-1">{t('securitySettings.password.updated')}</p>
                        </div>
                    </div>
                    <button
                        onClick={() => navigate('/reset-password')}
                        className="px-5 py-2.5 bg-white border border-slate-200 text-sm font-semibold text-slate-700 rounded-lg hover:bg-slate-50 transition-all"
                    >
                        {t('securitySettings.password.reset')}
                    </button>
                </div>
                <div className="pt-10 border-t border-slate-100">
                    <div className="mb-6">
                        <h3 className="text-base font-semibold text-slate-900">{t('securitySettings.sessions.title')}</h3>
                        <p className="text-sm text-slate-500 mt-1">{t('securitySettings.sessions.subtitle')}</p>
                    </div>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between p-5 bg-[#F8FAFC] border border-slate-200 rounded-xl">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 flex items-center justify-center text-slate-400 bg-white rounded-lg border border-slate-100">
                                    <DesktopWindows />
                                </div>
                                <div>
                                    <div className="flex items-center gap-3">
                                        <p className="text-sm font-semibold text-slate-900">{t('securitySettings.sessions.chromeDevice')}</p>
                                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded-full uppercase tracking-wider">{t('securitySettings.sessions.current')}</span>
                                    </div>
                                    <p className="text-xs text-slate-500 mt-0.5 font-medium">{t('securitySettings.sessions.chromeLocation')}</p>
                                </div>
                            </div>
                            <span className="text-xs font-semibold text-slate-400 mr-2">{t('securitySettings.sessions.activeNow')}</span>
                        </div>
                        <div className="flex items-center justify-between p-5 bg-[#F8FAFC] border border-slate-200 rounded-xl">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 flex items-center justify-center text-slate-400 bg-white rounded-lg border border-slate-100">
                                    <Smartphone />
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-slate-900">{t('securitySettings.sessions.iphoneDevice')}</p>
                                    <p className="text-xs text-slate-500 mt-0.5 font-medium">{t('securitySettings.sessions.iphoneLocation')}</p>
                                </div>
                            </div>
                            <button
                                onClick={handleSignOutSession}
                                className="text-xs font-bold text-rose-600 hover:text-rose-700 transition-colors uppercase tracking-tight px-3 py-1"
                            >
                                {t('securitySettings.sessions.signOut')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="bg-slate-50/50 px-10 py-6 border-t border-slate-200 flex items-center justify-end gap-6">
                <button className="text-sm font-semibold text-slate-500 hover:text-slate-800 transition-all">{t('securitySettings.cancel')}</button>
                <button
                    onClick={handleSave}
                    disabled={loading}
                    className={`px-8 py-3 rounded-lg text-sm font-bold transition-all shadow-md flex items-center gap-2 ${success ? 'bg-emerald-500 text-white' : 'bg-primary text-white hover:bg-blue-700'}`}
                >
                    {loading ? <Loader2 className="animate-spin" size={16} /> : success ? <Check size={16} /> : null}
                    {loading ? t('securitySettings.saving') : success ? t('securitySettings.saved') : t('securitySettings.save')}
                </button>
            </div>
        </div>
        <footer className="mt-12 text-center pb-12">
            <p className="text-slate-400 text-xs font-medium uppercase tracking-[0.1em]">© 2024 {t('appName')} {t('securitySettings.footer')}</p>
        </footer>
      </div>
    </SettingsLayout>
  );
};
